/**
 * Pure layout for the touch-campaign node canvas.
 *
 * A touch campaign is a sequence of "day" blocks (each holding one or more
 * action steps) separated by (usually week-long) waits. We walk the template's
 * `next.one` spine and turn each day into a positioned node. A `connect` step's
 * `wait_for_connect` node is FOLDED into its touch (rather than shown as its own
 * empty block): the touch renders "waits for connection", the spine continues on
 * the accepted (Yes) branch, and the not-accepted (No) branch is surfaced inline.
 * Kept free of React so the geometry is unit-testable without mounting a tree.
 */
import type CadenceStepState from '@poseidon/types/pg-gen/cadenceStepState';
import type CadenceStepType from '@poseidon/types/pg-gen/cadenceStepType';
import type { SiftConfig } from '@poseidon/types/types/sift.type';
import type { SwitchBand, SwitchBandOption } from '@poseidon/types/types/switchBand.type';
import { getTemplateStepDisplay } from '@poseidon/components/TemplateBlockCard';

import type { LocalGraphNode, LocalGraphNodes } from '~/components/CTGStep/CTGStep';
import { walkChainFrom } from '~/components/TemplateBlocksSection/templateBlocksLogic';

export type TouchGraph = { nodes: LocalGraphNodes; startId: string };

// Node box + spine geometry (content-space pixels, pre-zoom).
export const TOUCH_NODE_W = 320;
/** Trailing "add a touch" node is shorter than a real touch card. */
export const TOUCH_ADD_H = 56;
/**
 * Vertical gap BELOW a touch's card that holds the connector break — the insert "+"
 * stacked above the wait chip — before the next touch. (Was TOUCH_ROW_GAP.) Tuned to
 * the compact node scale: tall enough that the centered wait chip keeps its ~34px break
 * (EDGE_GAP_HALF) with a visible arrow segment above and below it, but no taller — long
 * connectors read as wasted space between the small nodes.
 */
export const TOUCH_WAIT_GAP = 80;
/** Back-compat alias — some tests/consumers still reference the old name. */
export const TOUCH_ROW_GAP = TOUCH_WAIT_GAP;
/**
 * Gap before the trailing "add a touch" node. Tighter than TOUCH_WAIT_GAP because
 * this connector carries no wait chip / insert "+" — just a short arrow.
 */
export const TOUCH_ADD_GAP = 28;
/** Padding around the whole content bounding box. */
export const TOUCH_CANVAS_PAD = 96;

// A connect touch's not-accepted (No) branch is its own path in a column to the
// LEFT of the main (accepted) spine.
const NO_COLUMN_GAP = 72; // horizontal gap between the No column and the main spine
export const TOUCH_NO_STUB_H = 48; // "+ if not accepted" affordance height

// Parallel fork arms run in columns to the RIGHT of the main spine.
const ARM_COLUMN_GAP = 72; // horizontal gap between adjacent arm columns
/** Vertical gap from a switch card's bottom down to its centered fan of arm columns. */
const SWITCH_FAN_GAP = 56;
/** Horizontal gap between a switch's arm columns (tighter than fork arms — they fan symmetrically). */
const SWITCH_ARM_GAP = 40;
/** First arm column offset from the spine's left edge (spine width + one gap). Exported
 * so the inline editor can float a new-branch popup in the first arm column, beside the
 * touch it forks from. */
export const ARM_COLUMN_X0 = TOUCH_NODE_W + ARM_COLUMN_GAP;
/**
 * Option values a delay node's PARALLEL fork arms use. The main spine is 'one' and the
 * wait_for_connect not-accepted ("No") branch is 'two', so arms start at 'three'. A
 * touch can fork up to FORK_ARM_OPTIONS.length arms, each on its own option/column.
 */
export const FORK_ARM_OPTIONS = ['three', 'four', 'five', 'six'] as const;
export const MAX_FORK_ARMS = FORK_ARM_OPTIONS.length;

/**
 * A `switch` step OWNS all 6 branch options as arms (one per band), unlike a plain
 * delay which reserves 'one' for the main spine and 'two' for the wfc No-branch. Column
 * order matches this list.
 */
export const SWITCH_ARM_OPTIONS = ['one', 'two', 'three', 'four', 'five', 'six'] as const;

/** Short caption for a switch arm's incoming elbow: the band's name, else a compact range, else the catch-all. */
export function switchArmLabel(band: SwitchBand): string {
  if (band.isCatchAll) return band.label != null && band.label.trim() !== '' ? band.label : 'Everything else';
  if (band.label != null && band.label.trim() !== '') return band.label;
  const { min, max } = band;
  if (min == null && max == null) return 'Any';
  if (min == null) return `< ${max}`;
  if (max == null) return `${min}+`;
  return `${min}–${max}`;
}
/** "+ add a parallel branch" affordance height (mirrors the No stub). */
export const TOUCH_ARM_STUB_H = 48;
/** Gap between the "candidates" header (above the first touch) and the first touch. */
const CANDIDATES_GAP = 64;

// Node height is content-driven (header + per-step rows + the "add step" footer)
// so multi-step touches aren't clipped by a fixed box.
// The card is content-sized and the connectors render BEHIND it, so node.h is kept a
// touch UNDER the real card height: the connector's top hides behind the card and it
// emerges flush from the bottom (no gap). Over-estimating would leave a visible gap.
const NODE_HEADER_H = 34; // "Week N" header (px-3 py-2, text-sm) + its border
const NODE_BODY_PAD = 0; // body is full-bleed now (no inset padding)
const STEP_ROW_H = 44; // full-width row: icon + type label + 1-line preview + py-2
const STEP_ROW_GAP = 1; // 1px divider (border-t) between stacked step rows
const EMPTY_BODY_H = 50; // "Empty touch" placeholder (px-3 py-5)
// One-line "waits for the invite to be accepted" note on a connect touch (border-t + py-1.5).
const WFC_HEADER_H = 26;
// Thin "add step" row at the bottom of a group with steps (border-t + py-1 + 11px text).
const ADD_STEP_ROW_H = 24;

// Extra height reserved on a step row for its per-contact status overlay (contact
// page only). Under-reserving overlaps the next touch (a real layout bug — see the
// STEP_PREVIEW note below), so we budget generously.
// The action-required "Mark done" button row (mt-1.5 + size-sm button).
const STATUS_ACTION_ROW_H = 36;
// A status message line (error / skip reason), clamped to 2 lines.
const STATUS_MSG_H = 30;

// Minimal (compact) rendering: the whole touch collapses to a single headerless
// row of step-type icon chips — no "Week N" header, no per-step preview text, no
// add-step footer. One fixed row regardless of step count. Kept a touch UNDER the
// real card height so connectors emerge flush from the bottom (see note above).
// Exported so the canvas can anchor a fork arm's connector to the icon row's vertical
// center (the row always sits at the top of a minimal card, above any action row).
export const MINIMAL_ROW_H = 32; // segmented button-group: size-8 (32px) chips + 1px border ≈ 34px card, kept a touch under so the connector emerges flush from its bottom (no dead gap)
const MINIMAL_EMPTY_H = 38; // "Empty" placeholder row
// Extra height under a minimal card's icon row for an action-required step's compact
// "Mark done" button (contact page only) — mirrors STATUS_ACTION_ROW_H on the full card.
const MINIMAL_ACTION_ROW_H = 30;
// Minimal cards hug their icon chips rather than spanning the full TOUCH_NODE_W —
// wide enough for ~2 same-day step icons (px-3 + two size-8 chips + gap). The
// branch columns tighten to match, so the whole minimal graph is compact.
const MINIMAL_NODE_W = 96;

/**
 * Resting width (content-space px) of a minimal touch's segmented step group — the
 * single bordered pill of `size-8` icon chips drawn by `TouchStepGroup`. Mirrors that
 * component's DOM: a 1px border on each side, one 32px chip per step, and a 1px hairline
 * divider between adjacent chips. The hover-revealed add-step "+" sits OUTSIDE the pill
 * (absolutely positioned), so it doesn't count. Used to anchor a fork arm's connector to
 * the pill's actual right edge: the card hugs its chips and is CENTERED in the wider
 * MINIMAL_NODE_W column, so the column edge (node.x + node.w) isn't the pill's edge.
 */
export function minimalGroupWidth(stepCount: number): number {
  if (stepCount <= 0) return 0;
  const CHIP = 32; // size-8
  const DIVIDER = 1; // w-px between adjacent chips
  const BORDER = 2; // 1px border on each side
  return BORDER + stepCount * CHIP + (stepCount - 1) * DIVIDER;
}

// Email / InMail rows render the message body in a wrapped block UNDER the
// one-line primary (the subject), and call rows show the script. STEP_ROW_H only
// budgets the primary line, so reserve extra height for that body — otherwise the
// taller card overlaps its below-region / the next touch (node.h drives the spine
// cursor). The clamp mirrors the `line-clamp-6` on the body span in TouchNodeCard.
const STEP_PREVIEW_MAX_LINES = 6;
const STEP_PREVIEW_LINE_H = 15; // text-[11px] leading-snug ≈ 15px per line
const STEP_PREVIEW_BODY_TOP_GAP = 2; // mt-0.5 above the body block
// Chars fitting on one preview line at TOUCH_NODE_W minus icon + horizontal padding
// (~272px at text-[11px]). Deliberately low so we estimate a touch MORE lines than
// fit — over-reserving leaves a small cosmetic gap, whereas under-reserving overlaps
// the next touch (a real bug).
const STEP_PREVIEW_CHARS_PER_LINE = 40;

/**
 * Estimated rendered line count for a body preview, capped at the clamp.
 *
 * The body renders with `whitespace-pre-wrap`, so the hard line breaks it
 * carries (`</p>`, `<br>` become real `\n` via htmlToSegments) each take a row
 * — including blank lines from `\n\n`. So we count per hard-line, adding the
 * soft-wrap rows within each, rather than dividing the whole char count once
 * (which under-counts a short multi-paragraph body and would overlap the next
 * touch). Still capped at STEP_PREVIEW_MAX_LINES to mirror `line-clamp-6`.
 */
function estimatePreviewLines(text: string): number {
  if (text.length === 0) return 0;
  const rows = text
    .split('\n')
    .reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / STEP_PREVIEW_CHARS_PER_LINE)), 0);
  return Math.min(STEP_PREVIEW_MAX_LINES, Math.max(1, rows));
}

/** Extra height a step needs for the wrapped body block below its primary line (0 when none). */
function stepBodyHeight(step: TouchGraphStep): number {
  const body = getTemplateStepDisplay(step).body;
  if (body == null || body.length === 0) return 0;
  return STEP_PREVIEW_BODY_TOP_GAP + estimatePreviewLines(body) * STEP_PREVIEW_LINE_H;
}

/**
 * Extra height a step row needs for its per-contact status overlay (0 when the
 * step has no contact status — i.e. the template page). Action-required adds a
 * "Mark done" button row; an error / skip-reason message adds a clamped line.
 */
function stepStatusHeight(step: TouchGraphStep): number {
  const status = step.contactStatus;
  if (status == null) return 0;
  let h = 0;
  if (status.state === 'action-required') h += STATUS_ACTION_ROW_H;
  if (status.errorMessage != null || (status.state === 'skipped' && status.skipReason != null)) h += STATUS_MSG_H;
  return h;
}

/**
 * Content-driven height of a touch CARD (header + step rows + optional wfc banner).
 * The "add step" affordance is no longer a card footer — it lives in the below-card
 * region — so the card ends at its last step row.
 */
function nodeHeight(steps: TouchGraphStep[], waitsForConnect: boolean, minimal: boolean): number {
  // Minimal: a single headerless icon row, fixed regardless of steps / wfc — plus a
  // compact mark-done button row when a step needs action. The branch structure
  // (no-branch, fork arms) still shows via the layout + edges.
  if (minimal) {
    if (steps.length === 0) return MINIMAL_EMPTY_H;
    const hasAction = steps.some((s) => s.contactStatus?.state === 'action-required');
    return MINIMAL_ROW_H + (hasAction ? MINIMAL_ACTION_ROW_H : 0);
  }
  let h: number;
  if (steps.length === 0) h = NODE_HEADER_H + NODE_BODY_PAD + EMPTY_BODY_H;
  else
    h =
      NODE_HEADER_H +
      NODE_BODY_PAD +
      steps.reduce((sum, s) => sum + STEP_ROW_H + stepBodyHeight(s) + stepStatusHeight(s), 0) +
      (steps.length - 1) * STEP_ROW_GAP +
      // The thin "add step" row shows on every group WITH steps.
      ADD_STEP_ROW_H;
  if (waitsForConnect) h += WFC_HEADER_H;
  return h;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * Per-contact execution status for a step, overlaid on the (otherwise
 * template-shaped) graph when it's rendered for a specific contact. Sourced from
 * the contact's own `cadence_step` rows and joined to a template graph node via
 * `templateStepId` → `node.id`. `null` on the standalone template page (no
 * contact context), so that surface renders exactly as before.
 */
export type TouchStepContactStatus = {
  /** The contact's own cadence-step id — what "mark done" acts on. */
  cadenceStepId: string;
  state: CadenceStepState;
  completedOn: Date | null;
  errorMessage: string | null;
  skipReason: string | null;
};

/** Lookup of per-contact status keyed by TEMPLATE step id (a graph node's id). */
export type StatusByTemplateStepId = Record<string, TouchStepContactStatus>;

/**
 * A block's action step, carrying the fields both the node preview
 * (`getTemplateStepDisplay`) and the inline editor (`CTSContentEditingPure`)
 * need — so the canvas + editor panel never have to reach back into the raw
 * graph.
 */
export type TouchGraphStep = {
  id: string;
  type: CadenceStepType;
  body2: string | null;
  subject: string | null;
  /**
   * Per-contact personalized body/subject (what actually sends), present only
   * when the graph is instance-driven (a contact's cadence) — null on the
   * template page. The node preview shows these in preference to `body2`/`subject`.
   */
  evaluatedBody: string | null;
  evaluatedSubject: string | null;
  postsToLike: number | null;
  cc: string | null;
  bcc: string | null;
  siftConditions: SiftConfig | null;
  onlyOpenLink: boolean;
  isPersonalized: boolean;
  personalizationPrompt: string | null;
  /** Email step only: continue the previous email's thread + reuse its subject (defaults true). */
  replyToPrevious: boolean;
  /** Connect step only: auto-withdraw the pending invite when the No branch resolves (defaults true). */
  autoWithdraw: boolean;
  /** PUBLIC ids of campaigns to enroll the contact into when this step finishes; empty when none. */
  triggerCadenceTemplateIds: string[];
  /** `switch` step only: the numeric attribute the switch routes on + its branch bands (up to 6 arms). */
  switchAttribute: string | null;
  switchBands: SwitchBand[] | null;
  /**
   * Human-readable names for `triggerCadenceTemplateIds`, resolved at model-build
   * time from the team's template list (falls back to the raw id when unknown /
   * not yet loaded). Empty when the step has no triggers. Used by the switch_campaign
   * card to show its destination without reaching back into the raw graph.
   */
  triggerCadenceTemplateNames: string[];
  /** Server-computed content validation error, surfaced as a red tint on the node. */
  error: string | null;
  /**
   * This contact's execution status for the step (done / action-required /
   * errored / skipped), or null when the graph has no contact context (template
   * page). Attached by `buildTouchGraphModel` from the passed status map.
   */
  contactStatus: TouchStepContactStatus | null;
};

/**
 * A touch is "terminal" (for MAIN-SPINE continuation) when it contains a `switch_campaign`
 * or a `switch` step. A `switch_campaign` moves the contact out of the campaign; a `switch`
 * fans the contact into branch arms — either way the MAIN SPINE ends here (nothing is added
 * after it on the spine; a switch's continuations live on its arms). Single source of truth
 * for the add-step gating shared by the node card (in-touch add) and the canvas (trailing add).
 */
export function touchStepsAreTerminal(steps: readonly TouchGraphStep[]): boolean {
  return steps.some((s) => s.type === 'switch_campaign' || s.type === 'switch');
}

export type TouchGraphNode = {
  /** Delay/day block id — the anchor for delay edits and insert-before. */
  blockId: string;
  /** 0-based order along the spine. */
  index: number;
  /** Short node badge, e.g. "Touch 1". */
  label: string;
  /** Natural "when" chip for the wait into this node ("next week", "in 2 weeks"), or null for the first. */
  waitLabel: string | null;
  /** Raw delay of this block, in ms. */
  delayMs: number;
  /** Whether the delay is calendar-week aligned (touch campaigns default to weeks). */
  alignWeek: boolean;
  /** Action steps inside the block (delay / wait_for_connect filtered out). */
  steps: TouchGraphStep[];
  /**
   * Last runnable step id in the block — the `afterStepId` anchor when adding a
   * step inside it. Falls back to the block id when the block has no runnables.
   */
  lastStepId: string;
  /**
   * Height of the region below the card. Always 0 now (the follow-on box that used
   * to live here was removed), but retained so the spine/edge stacking math and the
   * tests that read it keep compiling.
   */
  belowRegionH: number;
  /** True when this touch's `connect` step waits for the invite to be accepted. */
  waitsForConnect: boolean;
  /** The folded wait_for_connect node id — the anchor for accepted / not-accepted adds. */
  wfcId: string | null;
  /** The wait_for_connect's own delay — how long to wait for acceptance before the No branch runs. */
  wfcDelayMs: number;
  wfcAlignWeek: boolean;
  /** Which column this touch lives in: the main (accepted) spine, a not-accepted branch, or a parallel fork arm. */
  branch: 'main' | 'no' | 'fork';
  /** Block id of the touch this one flows from (for drawing the incoming edge), or null. */
  parentBlockId: string | null;
  /** What the incoming edge means — drives its label. `'fork'` is the head of a parallel arm. */
  edgeKind: 'next' | 'accepted' | 'notAccepted' | 'fork' | null;
  /**
   * True for the HEAD of a switch arm. Its incoming connector is the switch fan-bus (drawn from
   * `switchArmFans`) rather than a fork elbow, so the canvas skips the elbow edge for it.
   */
  isSwitchArm?: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
};

/** A "+ if not accepted" affordance shown in the No column when a connect touch has no No branch yet. */
export type TouchNoStub = {
  connectBlockId: string;
  wfcId: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

/** Trailing "Add a step" node at the bottom of a fork arm — the arm's own version of the main add-node. */
export type TouchArmAddNode = {
  /** The arm's tail block id — the `afterStepId` anchor for the appended step. */
  tailBlockId: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

/**
 * "+ add the first step" affordance for an EMPTY switch branch arm (a band with no
 * downstream steps yet). Anchors on the switch step's delay block; the new step routes
 * onto `option`. Each of a switch's up-to-6 bands gets a column — configured arms fan
 * out as node columns, empty ones show this stub so the user can build them.
 */
export type TouchSwitchArmAdd = {
  /** The switch STEP id — the `afterStepId` anchor for the new arm's first step (its child). */
  switchBlockId: string;
  /** Which branch option (one..six) the new step routes onto. */
  option: SwitchBandOption;
  /** The band's range caption, shown above the stub. */
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

/**
 * A switch's fan-out: the elbow "bus" that connects the switch card's bottom-center down to each of
 * its arm columns (centered under it), plus each arm's range caption. Drawn as SVG in the canvas —
 * mirrors the `/switches` branch graph so arms read as a connected fan, not floating boxes.
 */
export type TouchSwitchArmFan = {
  /** The `switch` step this fan belongs to — the target for inline range edits + add-branch. */
  switchStepId: string;
  /** Switch card center x + its bottom y (where the bus stem starts). */
  sourceCx: number;
  sourceBottomY: number;
  /** Shared horizontal bus y (between the switch and the arm row). */
  busY: number;
  /**
   * One entry per arm column, left→right: its center x, top y, range caption, and the underlying
   * band (option + raw bounds + catch-all flag) so the canvas can render an inline range editor.
   */
  arms: {
    cx: number;
    topY: number;
    label: string;
    option: SwitchBandOption;
    min: number | null;
    max: number | null;
    isCatchAll: boolean;
  }[];
  /** "Add a branch" affordance slot (rightmost column), or null when all 6 arms are used. */
  addBranch: { cx: number; topY: number } | null;
};

export type TouchGraphModel = {
  nodes: TouchGraphNode[];
  /** Empty-No-branch affordances (one per connect touch with no not-accepted steps). */
  noStubs: TouchNoStub[];
  /** Trailing "Add a step" nodes for each fork arm (one per arm). */
  armAddNodes: TouchArmAddNode[];
  /** "+ add the first step" stubs for empty switch branch arms (one per unconfigured band). */
  switchArmAdds: TouchSwitchArmAdd[];
  /** Fan-out connectors (switch card → its arm columns) with range captions; one per switch touch. */
  switchArmFans: TouchSwitchArmFan[];
  /** Position of the trailing "add a touch" node (main spine). */
  addNode: { x: number; y: number; w: number; h: number };
  /** The "candidates" header rect above the first touch, or null when not shown. */
  candidates: { x: number; y: number; w: number; h: number } | null;
  contentW: number;
  contentH: number;
};

/**
 * Node badge reflecting WHEN a touch fires — "Week N" / "Day N" — from its
 * cumulative delay off the campaign start, in the campaign's delay unit.
 */
export function touchTimingLabel(cumulativeMs: number, useWeeks: boolean): string {
  if (useWeeks) return `Week ${Math.round(cumulativeMs / WEEK_MS) + 1}`;
  return `Day ${Math.round(cumulativeMs / DAY_MS) + 1}`;
}

/** Human quantity for a wait ("1 week", "3 days"). `null` when there's no wait. */
export function formatWaitLabel(delayMs: number, alignWeek: boolean): string | null {
  if (delayMs <= 0) return null;
  if (alignWeek || delayMs % WEEK_MS === 0) {
    const weeks = Math.max(1, Math.round(delayMs / WEEK_MS));
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  const days = Math.max(1, Math.round(delayMs / DAY_MS));
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Chip label for the wait leading into the NEXT touch on the spine. Reads as a
 * natural "when" phrase ("next week", "in 2 weeks", "next day", "in 3 days")
 * rather than the imperative "wait N". `null` when there's no wait.
 */
export function formatWaitChipLabel(delayMs: number, alignWeek: boolean): string | null {
  if (delayMs <= 0) return null;
  if (alignWeek || delayMs % WEEK_MS === 0) {
    const weeks = Math.max(1, Math.round(delayMs / WEEK_MS));
    return weeks === 1 ? 'wait until next week' : `wait ${weeks} weeks`;
  }
  const days = Math.max(1, Math.round(delayMs / DAY_MS));
  return days === 1 ? 'wait until next day' : `wait ${days} days`;
}

/**
 * Build the positioned node model. The accepted flow runs down the main spine;
 * a connect touch's not-accepted (No) branch runs down its own column to the
 * LEFT, joined by a "not accepted" edge. Empty No branches get a "+ if not
 * accepted" stub.
 */
export function buildTouchGraphModel(
  graph: TouchGraph | null,
  candidates?: { w: number; h: number } | null,
  /** Compact headerless icon nodes (Minimal) instead of full step-preview cards. */
  minimal: boolean = false,
  /**
   * Per-contact status keyed by template step id. When present (contact page), each
   * step gets its `contactStatus`; absent/null on the template page → no overlay.
   */
  statusByTemplateStepId: StatusByTemplateStepId | null = null,
  /**
   * Template PUBLIC id → name, used to resolve a switch_campaign step's destination
   * for display. Optional — omit on surfaces that don't render switch_campaign.
   */
  campaignNameById: ReadonlyMap<string, string> | null = null,
): TouchGraphModel {
  // Node + column width — narrow in minimal so cards hug their icons and the No /
  // arm columns pack in tighter. All spine/column/edge math derives from `nodeW`.
  const nodeW = minimal ? MINIMAL_NODE_W : TOUCH_NODE_W;
  const armColX0 = nodeW + ARM_COLUMN_GAP;
  const armColStep = nodeW + ARM_COLUMN_GAP;

  // Reserve room above the first touch for the "candidates" header (when shown).
  const headerH = candidates != null ? candidates.h + CANDIDATES_GAP : 0;
  const topY = TOUCH_CANVAS_PAD + headerH;

  if (graph == null) {
    return finalizeModel({
      nodes: [],
      noStubs: [],
      armAddNodes: [],
      switchArmAdds: [],
      switchArmFans: [],
      addNode: { x: TOUCH_CANVAS_PAD, y: topY, w: nodeW, h: TOUCH_ADD_H },
      spineCx: TOUCH_CANVAS_PAD + nodeW / 2,
      maxBottom: topY + TOUCH_ADD_H,
      candidates,
    });
  }

  const spine = walkTouchSpine(graph, graph.startId, statusByTemplateStepId, campaignNameById);
  const hasNoColumn = spine.some((t) => t.waitsForConnect);
  const noX = TOUCH_CANVAS_PAD;
  // Shift the main spine right to make room for the No column only when needed,
  // so plain (no-connect) campaigns stay centered as before.
  const mainX = hasNoColumn ? TOUCH_CANVAS_PAD + nodeW + NO_COLUMN_GAP : TOUCH_CANVAS_PAD;

  const nodes: TouchGraphNode[] = [];
  const noStubs: TouchNoStub[] = [];

  // Badge unit: weeks unless the spine has a clearly day-based delay (not week-aligned
  // and not a whole number of weeks). Most touch campaigns are weekly.
  const useWeeks = !spine.some((t) => t.delayMs > 0 && !t.alignWeek && t.delayMs % WEEK_MS !== 0);

  // Main (accepted) spine.
  let cursorY = topY;
  let cumulativeMs = 0;
  spine.forEach((touch, i) => {
    const prev = i > 0 ? spine[i - 1] : null;
    cumulativeMs += touch.delayMs; // delay leading INTO this touch (0 for the first)
    const node = makeNode({
      touch,
      index: i,
      label: touchTimingLabel(cumulativeMs, useWeeks),
      branch: 'main',
      parentBlockId: prev?.blockId ?? null,
      edgeKind: prev == null ? null : prev.waitsForConnect ? 'accepted' : 'next',
      x: mainX,
      y: cursorY,
      minimal,
    });
    nodes.push(node);
    cursorY += node.h + node.belowRegionH + TOUCH_WAIT_GAP;
  });
  // Pull the trailing add-node up to the tighter add-gap (the loop left a full
  // TOUCH_WAIT_GAP below the last touch's region).
  const addY = nodes.length > 0 ? cursorY - TOUCH_WAIT_GAP + TOUCH_ADD_GAP : cursorY;
  let maxBottom = addY + TOUCH_ADD_H;

  // Not-accepted branches — one column per connect touch, aligned with it.
  for (const touch of spine) {
    if (!touch.waitsForConnect || touch.wfcId == null) continue;
    const connectNode = nodes.find((n) => n.blockId === touch.blockId);
    if (connectNode == null) continue;
    const noBlocks = touch.noBranchStartId != null ? walkChainFrom(graph, touch.noBranchStartId) : [];
    // The No branch forks off BELOW the connect touch's region, aligned with the
    // accepted continuation but in the left column.
    const branchY = connectNode.y + connectNode.h + connectNode.belowRegionH + TOUCH_WAIT_GAP;

    if (noBlocks.length === 0) {
      noStubs.push({
        connectBlockId: touch.blockId,
        wfcId: touch.wfcId,
        x: noX,
        y: branchY,
        w: nodeW,
        h: TOUCH_NO_STUB_H,
      });
      maxBottom = Math.max(maxBottom, branchY + TOUCH_NO_STUB_H);
      continue;
    }

    let noY = branchY;
    noBlocks.forEach((block, j) => {
      const node = makeNode({
        touch: {
          blockId: block.id,
          delayMs: block.delay,
          alignWeek: block.alignWeek ?? false,
          steps: actionStepsOf(block.group, graph, statusByTemplateStepId, campaignNameById),
          waitsForConnect: false,
          wfcId: null,
          wfcDelayMs: 0,
          wfcAlignWeek: false,
          noBranchStartId: null,
          forkArmStartIds: [],
          isSwitch: false,
          switchStepId: null,
          switchArms: [],
        },
        index: j,
        label: j === 0 ? 'If not accepted' : 'Then',
        branch: 'no',
        parentBlockId: j === 0 ? touch.blockId : noBlocks[j - 1]!.id,
        edgeKind: j === 0 ? 'notAccepted' : 'next',
        x: noX,
        y: noY,
        minimal,
      });
      nodes.push(node);
      noY += node.h + node.belowRegionH + TOUCH_WAIT_GAP;
    });
    maxBottom = Math.max(maxBottom, noY);
  }

  // Fork arms — each a FIRST-CLASS mini-spine in its own column to the RIGHT of the
  // main spine. Identical to the main spine (timing badges, per-edge wait chips, a
  // trailing add-node) — the ONLY difference is where it starts: the branch-start
  // edge hangs off its fork touch. Reuses walkChainFrom to walk the arm chain.
  let armCol = 0;
  const armAddNodes: TouchArmAddNode[] = [];
  const switchArmAdds: TouchSwitchArmAdd[] = [];
  const switchArmFans: TouchSwitchArmFan[] = [];
  // Connect touches inside arms need a No-branch column too — collected here and
  // rendered (to the LEFT of the arm's column) after all arm nodes are placed.
  const armConnects: { touch: SpineTouch; connectNode: TouchGraphNode; armX: number }[] = [];
  for (const touch of spine) {
    if (touch.forkArmStartIds.length === 0) continue;
    const forkNode = nodes.find((n) => n.blockId === touch.blockId);
    if (forkNode == null) continue;
    const branchY = forkNode.y + forkNode.h + forkNode.belowRegionH + TOUCH_WAIT_GAP;
    for (const armStartId of touch.forkArmStartIds) {
      const armX = mainX + armColX0 + armCol * armColStep;
      armCol += 1;
      // Walk the arm the SAME way as the main spine so a `connect` step folds its
      // wait_for_connect (accepted continuation inline, not-accepted as its own column).
      const armTouches = walkTouchSpine(graph, armStartId, statusByTemplateStepId, campaignNameById);
      const armUseWeeks = !armTouches.some((t) => t.delayMs > 0 && !t.alignWeek && t.delayMs % WEEK_MS !== 0);
      let armY = branchY;
      let armCumulative = 0;
      armTouches.forEach((armTouch, j) => {
        armCumulative += armTouch.delayMs; // delay leading INTO this arm step
        const prev = j > 0 ? armTouches[j - 1] : null;
        const node = makeNode({
          touch: armTouch,
          index: j,
          // Timing badge like the main spine ("Day N" / "Week N"), relative to the
          // branch point — NOT the "After group" / "Then" placeholders.
          label: touchTimingLabel(armCumulative, armUseWeeks),
          branch: 'fork',
          parentBlockId: j === 0 ? touch.blockId : prev!.blockId,
          edgeKind: j === 0 ? 'fork' : prev!.waitsForConnect ? 'accepted' : 'next',
          x: armX,
          y: armY,
          minimal,
        });
        nodes.push(node);
        if (armTouch.waitsForConnect && armTouch.wfcId != null)
          armConnects.push({ touch: armTouch, connectNode: node, armX });
        armY += node.h + node.belowRegionH + TOUCH_WAIT_GAP;
      });
      // Trailing "Add a step" for this arm, pulled up to the tighter add-gap — same
      // as the main spine's trailing add-node.
      const tail = armTouches[armTouches.length - 1];
      if (tail != null) {
        armAddNodes.push({
          x: armX,
          y: armY - TOUCH_WAIT_GAP + TOUCH_ADD_GAP,
          w: nodeW,
          h: TOUCH_ADD_H,
          tailBlockId: tail.blockId,
        });
      }
      maxBottom = Math.max(maxBottom, armY + TOUCH_ADD_H);
    }
  }

  // Switch arms — a `switch` step fans the contact into up-to-6 branch arms by numeric range.
  // The arm columns are CENTERED under the switch card and connected by an elbow fan-bus (drawn in
  // the canvas), mirroring the `/switches` branch graph. Unlike the main-spine fork loop above,
  // an arm is laid out RECURSIVELY as a full sub-campaign: its own spine, its connect No-branches,
  // and its parallel fork arms all render (so an arm that is itself a call-spine ∥ email-fork ∥
  // message-fork campaign draws in full, not just its spine). An empty band shows a "+ add" stub.
  const switchColStep = nodeW + SWITCH_ARM_GAP;

  // A connect touch's not-accepted branch, in a single column at `noX`. Returns its bottom Y.
  const layoutArmNoBranch = (t: SpineTouch, connectNode: TouchGraphNode, noX: number): number => {
    if (t.wfcId == null) return connectNode.y;
    const branchY = connectNode.y + connectNode.h + connectNode.belowRegionH + TOUCH_WAIT_GAP;
    const noBlocks = t.noBranchStartId != null ? walkChainFrom(graph, t.noBranchStartId) : [];
    if (noBlocks.length === 0) {
      noStubs.push({ connectBlockId: t.blockId, wfcId: t.wfcId, x: noX, y: branchY, w: nodeW, h: TOUCH_NO_STUB_H });
      return branchY + TOUCH_NO_STUB_H;
    }
    let noY = branchY;
    noBlocks.forEach((block, j) => {
      const node = makeNode({
        touch: {
          blockId: block.id,
          delayMs: block.delay,
          alignWeek: block.alignWeek ?? false,
          steps: actionStepsOf(block.group, graph, statusByTemplateStepId, campaignNameById),
          waitsForConnect: false,
          wfcId: null,
          wfcDelayMs: 0,
          wfcAlignWeek: false,
          noBranchStartId: null,
          forkArmStartIds: [],
          isSwitch: false,
          switchStepId: null,
          switchArms: [],
        },
        index: j,
        label: j === 0 ? 'If not accepted' : 'Then',
        branch: 'no',
        parentBlockId: j === 0 ? t.blockId : noBlocks[j - 1]!.id,
        edgeKind: j === 0 ? 'notAccepted' : 'next',
        x: noX,
        y: noY,
        minimal,
      });
      nodes.push(node);
      noY += node.h + node.belowRegionH + TOUCH_WAIT_GAP;
    });
    return noY;
  };

  // Columns a switch-arm subtree occupies: its spine (1) + one per connect No-branch +
  // each fork arm's columns (recursive). Kept in lockstep with layoutArmSubtree so arms
  // get spaced without overlapping.
  const countArmColumns = (startId: string): number => {
    const touches = walkTouchSpine(graph, startId, statusByTemplateStepId, campaignNameById);
    let cols = 1;
    for (const t of touches) {
      if (t.waitsForConnect && t.wfcId != null) cols += 1;
      for (const forkStart of t.forkArmStartIds) cols += countArmColumns(forkStart);
    }
    return cols;
  };

  // Contact view only: did THIS contact go down this arm? True if any step in the arm
  // carries a live (non-canceled) per-contact status. A migrated contact only has
  // instances on its taken arm; a freshly-spawned one has the losing arms canceled —
  // either way, the taken arm is the one with a non-canceled status.
  const armIsTaken = (startId: string): boolean => {
    const touches = walkTouchSpine(graph, startId, statusByTemplateStepId, campaignNameById);
    for (const t of touches) {
      for (const step of t.steps) {
        if (step.contactStatus != null && step.contactStatus.state !== 'canceled') return true;
      }
      // The only live status can sit on a connect's No-branch or a nested fork arm — recurse
      // into both (mirrors countArmColumns) so an arm whose spine is status-less isn't missed.
      if (t.waitsForConnect && t.noBranchStartId != null) {
        for (const block of walkChainFrom(graph, t.noBranchStartId)) {
          for (const stepId of block.group) {
            const st = statusByTemplateStepId?.[stepId];
            if (st != null && st.state !== 'canceled') return true;
          }
        }
      }
      for (const forkStart of t.forkArmStartIds) if (armIsTaken(forkStart)) return true;
    }
    return false;
  };

  // Lay out a switch-arm subtree: its spine in column `baseCol` (x = baseX + col*step),
  // its No-branches + fork arms cascading into columns to the RIGHT. Returns columns used.
  const layoutArmSubtree = (a: {
    startId: string;
    baseCol: number;
    baseX: number;
    baseY: number;
    parentBlockId: string;
    isHead: boolean;
  }): { cols: number; bottom: number } => {
    const touches = walkTouchSpine(graph, a.startId, statusByTemplateStepId, campaignNameById);
    const armUseWeeks = !touches.some((t) => t.delayMs > 0 && !t.alignWeek && t.delayMs % WEEK_MS !== 0);
    const spineX = a.baseX + a.baseCol * switchColStep;
    let y = a.baseY;
    let cumulative = 0;
    let nextCol = a.baseCol + 1;
    let bottom = a.baseY;
    touches.forEach((t, j) => {
      cumulative += t.delayMs;
      const prev = j > 0 ? touches[j - 1] : null;
      const node = makeNode({
        touch: t,
        index: j,
        label: touchTimingLabel(cumulative, armUseWeeks),
        branch: 'fork',
        // The arm head's incoming edge is the fan-bus (isSwitchArm keeps it out of fork elbows).
        parentBlockId: j === 0 ? a.parentBlockId : prev!.blockId,
        edgeKind: j === 0 ? 'fork' : prev!.waitsForConnect ? 'accepted' : 'next',
        isSwitchArm: j === 0 && a.isHead,
        x: spineX,
        y,
        minimal,
      });
      nodes.push(node);
      const belowY = node.y + node.h + node.belowRegionH + TOUCH_WAIT_GAP;
      if (t.waitsForConnect && t.wfcId != null) {
        bottom = Math.max(bottom, layoutArmNoBranch(t, node, a.baseX + nextCol * switchColStep));
        nextCol += 1;
      }
      for (const forkStart of t.forkArmStartIds) {
        const r = layoutArmSubtree({
          startId: forkStart,
          baseCol: nextCol,
          baseX: a.baseX,
          baseY: belowY,
          parentBlockId: t.blockId,
          isHead: false,
        });
        nextCol += r.cols;
        bottom = Math.max(bottom, r.bottom);
      }
      y = belowY;
      bottom = Math.max(bottom, y);
    });
    const tail = touches[touches.length - 1];
    if (tail != null) {
      armAddNodes.push({
        x: spineX,
        y: y - TOUCH_WAIT_GAP + TOUCH_ADD_GAP,
        w: nodeW,
        h: TOUCH_ADD_H,
        tailBlockId: tail.blockId,
      });
    }
    return { cols: nextCol - a.baseCol, bottom };
  };

  for (const touch of spine) {
    if (!touch.isSwitch) continue;
    const switchNode = nodes.find((n) => n.blockId === touch.blockId);
    if (switchNode == null) continue;

    // Contact view: once a contact has been routed into one arm, the others can never
    // run, so collapse to just the taken arm (the rest are distracting dead branches).
    // Template editor (statusByTemplateStepId null) shows every band as before.
    const takenArms =
      statusByTemplateStepId != null
        ? touch.switchArms.filter((arm) => arm.startId != null && armIsTaken(arm.startId))
        : [];
    const soloArm = takenArms.length === 1 ? takenArms[0]! : null;
    const renderArms = soloArm != null ? [soloArm] : touch.switchArms;

    // No "add a branch" slot when focused on a single taken arm (contact view is read-only there).
    const canAddBranch = soloArm == null && touch.switchArms.length < 6;
    // Each arm's column width (empty bands are a single stub column) drives the spacing so
    // an arm's fork columns never overlap the next arm.
    const armColsList = renderArms.map((arm) => (arm.startId == null ? 1 : countArmColumns(arm.startId)));
    const totalCols = armColsList.reduce((sum, c) => sum + c, 0) + (canAddBranch ? 1 : 0);
    const span = totalCols * nodeW + Math.max(0, totalCols - 1) * SWITCH_ARM_GAP;
    const switchCx = switchNode.x + switchNode.w / 2;
    const switchBottom = switchNode.y + switchNode.h + switchNode.belowRegionH;
    const branchY = switchBottom + SWITCH_FAN_GAP;
    const firstArmX = switchCx - span / 2;
    const fanArms: TouchSwitchArmFan['arms'] = [];

    let colCursor = 0;
    renderArms.forEach((arm, ai) => {
      const armX = firstArmX + colCursor * switchColStep;
      fanArms.push({
        cx: armX + nodeW / 2,
        topY: branchY,
        label: arm.label,
        option: arm.option,
        min: arm.min,
        max: arm.max,
        isCatchAll: arm.isCatchAll,
      });

      if (arm.startId == null) {
        // Empty band: a first-step affordance, anchored on the SWITCH step (its children are arms).
        switchArmAdds.push({
          switchBlockId: touch.switchStepId ?? touch.blockId,
          option: arm.option,
          label: arm.label,
          x: armX,
          y: branchY,
          w: nodeW,
          h: TOUCH_ADD_H,
        });
        maxBottom = Math.max(maxBottom, branchY + TOUCH_ADD_H);
      } else {
        const r = layoutArmSubtree({
          startId: arm.startId,
          baseCol: colCursor,
          baseX: firstArmX,
          baseY: branchY,
          parentBlockId: touch.blockId,
          isHead: true,
        });
        maxBottom = Math.max(maxBottom, r.bottom + TOUCH_ADD_H);
      }
      colCursor += armColsList[ai]!;
    });

    if (touch.switchStepId != null) {
      const addBranch = canAddBranch
        ? { cx: firstArmX + colCursor * switchColStep + nodeW / 2, topY: branchY }
        : null;
      if (addBranch != null) maxBottom = Math.max(maxBottom, branchY + TOUCH_ADD_H);
      switchArmFans.push({
        switchStepId: touch.switchStepId,
        sourceCx: switchCx,
        sourceBottomY: switchBottom,
        busY: (switchBottom + branchY) / 2,
        arms: fanArms,
        addBranch,
      });
    }
  }

  // No-branch column for each connect touch INSIDE an arm — mirrors the main-spine
  // No-branch loop, placed one column to the LEFT of the arm it belongs to.
  for (const { touch, connectNode, armX } of armConnects) {
    if (touch.wfcId == null) continue;
    // To the RIGHT of the arm (the main spine sits to the left, so a left column
    // would collide with it).
    const armNoX = armX + nodeW + NO_COLUMN_GAP;
    const noBlocks = touch.noBranchStartId != null ? walkChainFrom(graph, touch.noBranchStartId) : [];
    const branchY = connectNode.y + connectNode.h + connectNode.belowRegionH + TOUCH_WAIT_GAP;
    if (noBlocks.length === 0) {
      noStubs.push({
        connectBlockId: touch.blockId,
        wfcId: touch.wfcId,
        x: armNoX,
        y: branchY,
        w: nodeW,
        h: TOUCH_NO_STUB_H,
      });
      maxBottom = Math.max(maxBottom, branchY + TOUCH_NO_STUB_H);
      continue;
    }
    let noY = branchY;
    noBlocks.forEach((block, j) => {
      const node = makeNode({
        touch: {
          blockId: block.id,
          delayMs: block.delay,
          alignWeek: block.alignWeek ?? false,
          steps: actionStepsOf(block.group, graph, statusByTemplateStepId, campaignNameById),
          waitsForConnect: false,
          wfcId: null,
          wfcDelayMs: 0,
          wfcAlignWeek: false,
          noBranchStartId: null,
          forkArmStartIds: [],
          isSwitch: false,
          switchStepId: null,
          switchArms: [],
        },
        index: j,
        label: j === 0 ? 'If not accepted' : 'Then',
        branch: 'no',
        parentBlockId: j === 0 ? touch.blockId : noBlocks[j - 1]!.id,
        edgeKind: j === 0 ? 'notAccepted' : 'next',
        x: armNoX,
        y: noY,
        minimal,
      });
      nodes.push(node);
      noY += node.h + node.belowRegionH + TOUCH_WAIT_GAP;
    });
    maxBottom = Math.max(maxBottom, noY);
  }

  return finalizeModel({
    nodes,
    noStubs,
    armAddNodes,
    switchArmAdds,
    switchArmFans,
    addNode: { x: mainX, y: addY, w: nodeW, h: TOUCH_ADD_H },
    spineCx: mainX + nodeW / 2,
    maxBottom,
    candidates,
  });
}

/**
 * Place the optional candidates header (centered on the spine), then normalize x
 * so nothing sits left of the padding (a wide candidates flow can push left), and
 * compute the content bounds.
 */
function finalizeModel(params: {
  nodes: TouchGraphNode[];
  noStubs: TouchNoStub[];
  armAddNodes: TouchArmAddNode[];
  switchArmAdds: TouchSwitchArmAdd[];
  switchArmFans: TouchSwitchArmFan[];
  addNode: { x: number; y: number; w: number; h: number };
  spineCx: number;
  maxBottom: number;
  candidates?: { w: number; h: number } | null;
}): TouchGraphModel {
  const { nodes, noStubs, armAddNodes, switchArmAdds, switchArmFans, spineCx, maxBottom } = params;
  let addNode = params.addNode;
  let candidates: TouchGraphModel['candidates'] =
    params.candidates != null
      ? { x: spineCx - params.candidates.w / 2, y: TOUCH_CANVAS_PAD, w: params.candidates.w, h: params.candidates.h }
      : null;

  const xs = [
    ...nodes.map((n) => n.x),
    ...noStubs.map((s) => s.x),
    ...armAddNodes.map((a) => a.x),
    ...switchArmAdds.map((a) => a.x),
    addNode.x,
  ];
  if (candidates != null) xs.push(candidates.x);
  const shift = Math.min(...xs) < TOUCH_CANVAS_PAD ? TOUCH_CANVAS_PAD - Math.min(...xs) : 0;
  if (shift !== 0) {
    for (const n of nodes) n.x += shift;
    for (const s of noStubs) s.x += shift;
    for (const a of armAddNodes) a.x += shift;
    for (const a of switchArmAdds) a.x += shift;
    for (const f of switchArmFans) {
      f.sourceCx += shift;
      for (const arm of f.arms) arm.cx += shift;
      if (f.addBranch != null) f.addBranch.cx += shift;
    }
    addNode = { ...addNode, x: addNode.x + shift };
    if (candidates != null) candidates = { ...candidates, x: candidates.x + shift };
  }

  const rights = [
    ...nodes.map((n) => n.x + n.w),
    ...noStubs.map((s) => s.x + s.w),
    ...armAddNodes.map((a) => a.x + a.w),
    ...switchArmAdds.map((a) => a.x + a.w),
    // The add-branch slot is a nodeW-wide column centered on its cx — count its right edge.
    ...switchArmFans.map((f) => (f.addBranch != null ? f.addBranch.cx + TOUCH_NODE_W / 2 : 0)),
    addNode.x + addNode.w,
  ];
  if (candidates != null) rights.push(candidates.x + candidates.w);
  return {
    nodes,
    noStubs,
    armAddNodes,
    switchArmAdds,
    switchArmFans,
    addNode,
    candidates,
    contentW: Math.max(...rights) + TOUCH_CANVAS_PAD,
    contentH: maxBottom + TOUCH_CANVAS_PAD,
  };
}

/** One touch, with any folded wait_for_connect (No branch captured by start id). */
type SpineTouch = {
  blockId: string;
  delayMs: number;
  alignWeek: boolean;
  steps: TouchGraphStep[];
  waitsForConnect: boolean;
  wfcId: string | null;
  wfcDelayMs: number;
  wfcAlignWeek: boolean;
  noBranchStartId: string | null;
  /**
   * Start ids of PARALLEL arms forking off this touch — the delay node's extra
   * group-node children (options beyond the main `next.one` spine). Each arm runs
   * independently on its own day/week schedule and is laid out in its own column.
   */
  forkArmStartIds: string[];
  /**
   * True when this touch's group contains a `switch` step — a decision node that fans the
   * contact into branch arms by a numeric range. The main spine STOPS here (every path is
   * an arm); `switchArms` describes the up-to-6 branches.
   */
  isSwitch: boolean;
  /** The `switch` step's id (carries `switchBands`) — the target for inline range edits. */
  switchStepId: string | null;
  /**
   * Switch arms, one per band, in column order. `startId` is the arm's head delay block
   * (null when the band has no downstream steps yet — the layout shows a "+ add" stub).
   * `min`/`max` are the band's raw bounds (for the inline range editor); `isCatchAll` marks
   * the "everything else" arm (no bounds, not deletable).
   */
  switchArms: {
    option: SwitchBandOption;
    startId: string | null;
    label: string;
    min: number | null;
    max: number | null;
    isCatchAll: boolean;
  }[];
};

/** Assemble a positioned node from a touch + its edge/branch context. */
function makeNode(args: {
  touch: SpineTouch;
  index: number;
  label: string;
  branch: 'main' | 'no' | 'fork';
  parentBlockId: string | null;
  edgeKind: TouchGraphNode['edgeKind'];
  /** True for a switch arm head — its incoming connector is the fan-bus, not a fork elbow. */
  isSwitchArm?: boolean;
  x: number;
  y: number;
  minimal: boolean;
}): TouchGraphNode {
  const { touch, index, label, branch, parentBlockId, edgeKind, isSwitchArm, x, y, minimal } = args;
  const lastStep = touch.steps[touch.steps.length - 1];
  return {
    blockId: touch.blockId,
    index,
    label,
    // Column-entry edges (first touch / not-accepted) show no wait; a 'next',
    // 'accepted', or 'fork' (branch-start) continuation carries its block's wait —
    // so the branch-start delay is visible + editable just like a normal step.
    waitLabel:
      edgeKind === 'next' || edgeKind === 'accepted' || edgeKind === 'fork'
        ? formatWaitChipLabel(touch.delayMs, touch.alignWeek)
        : null,
    delayMs: touch.delayMs,
    alignWeek: touch.alignWeek,
    steps: touch.steps,
    lastStepId: lastStep?.id ?? touch.blockId,
    belowRegionH: 0,
    waitsForConnect: touch.waitsForConnect,
    wfcId: touch.wfcId,
    wfcDelayMs: touch.wfcDelayMs,
    wfcAlignWeek: touch.wfcAlignWeek,
    branch,
    parentBlockId,
    edgeKind,
    isSwitchArm: isSwitchArm ?? false,
    x,
    y,
    w: minimal ? MINIMAL_NODE_W : TOUCH_NODE_W,
    h: nodeHeight(touch.steps, touch.waitsForConnect, minimal),
  };
}

/**
 * Walk the `next.one` spine, emitting one touch per delay block. When a block is
 * followed by a `wait_for_connect`, fold it in: mark the touch as waiting for
 * acceptance, capture the No-branch start (`next.two`), and continue the spine on
 * the accepted branch (`next.one`) instead of emitting the wfc as its own block.
 */
function walkTouchSpine(
  graph: TouchGraph,
  startId: string = graph.startId,
  statusByTemplateStepId: StatusByTemplateStepId | null = null,
  campaignNameById: ReadonlyMap<string, string> | null = null,
): SpineTouch[] {
  const out: SpineTouch[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = startId;

  while (cur != null && !seen.has(cur)) {
    seen.add(cur);
    const node: LocalGraphNode | undefined = graph.nodes[cur];
    if (node == null) break;

    if (node.type !== 'delay' && node.type !== 'wait_for_connect') {
      // Runnable at the spine root — shouldn't happen; skip past it defensively.
      cur = node.next.one ?? undefined;
      continue;
    }

    let waitsForConnect = false;
    let wfcId: string | null = null;
    let wfcDelayMs = 0;
    let wfcAlignWeek = false;
    let noBranchStartId: string | null = null;
    let next = node.next.one;

    const nextNode = next != null ? graph.nodes[next] : undefined;
    if (nextNode?.type === 'wait_for_connect') {
      waitsForConnect = true;
      wfcId = nextNode.id;
      wfcDelayMs = nextNode.delay;
      wfcAlignWeek = nextNode.delayAlignWeek;
      noBranchStartId = nextNode.next.two ?? null;
      next = nextNode.next.one; // continue on the accepted branch
    }

    const steps = actionStepsOf(node.group, graph, statusByTemplateStepId, campaignNameById);

    // A `switch` is a BRANCH node sitting at this delay's `next.one` (like a folded wait_for_connect):
    // it reads a number and fans the contact into one of up-to-6 arms, so the MAIN SPINE STOPS here.
    // Fold it into this touch — render it as the touch's step, and read its arms from the SWITCH's
    // OWN `next[option]` (arms are the switch's children, not the delay's).
    // Resolve the branch node from the CURRENT `next` — a wait_for_connect fold above advanced `next`
    // to the accepted branch, so `nextNode` (captured pre-fold) is stale for a `delay → wfc → switch`.
    const branchNode = next != null ? graph.nodes[next] : undefined;
    let isSwitch = false;
    let switchStepId: string | null = null;
    let switchArms: SpineTouch['switchArms'] = [];
    if (branchNode?.type === 'switch') {
      const switchTouchStep = toTouchStep(branchNode, statusByTemplateStepId, campaignNameById);
      if (switchTouchStep.switchBands != null && switchTouchStep.switchBands.length > 0) {
        isSwitch = true;
        switchStepId = branchNode.id;
        steps.push(switchTouchStep); // the switch renders as this touch's step
        const order = (o: SwitchBandOption) => SWITCH_ARM_OPTIONS.indexOf(o);
        switchArms = [...switchTouchStep.switchBands]
          .sort((a, b) => order(a.option) - order(b.option))
          .map((band) => ({
            option: band.option,
            startId: branchNode.next[band.option] ?? null,
            label: switchArmLabel(band),
            min: band.min,
            max: band.max,
            isCatchAll: band.isCatchAll,
          }));
        next = undefined; // spine ends at the switch
      }
    }

    // Parallel fork arms: a plain delay node's group-node children on options
    // OTHER than the main `next.one` spine. Each is the head of an independent arm.
    // (A wait_for_connect's `next.two` is its No branch, handled above — that lives
    // on the wfc node, not on this delay node, so it isn't picked up here.)
    // A switch owns all six options as arms, so fork-arm collection is skipped for it.
    const forkArmStartIds = isSwitch ? [] : groupNodeArms(node, graph);

    out.push({
      blockId: node.id,
      delayMs: node.delay,
      alignWeek: node.delayAlignWeek,
      steps,
      waitsForConnect,
      wfcId,
      wfcDelayMs,
      wfcAlignWeek,
      noBranchStartId,
      forkArmStartIds,
      isSwitch,
      switchStepId,
      switchArms,
    });
    cur = next ?? undefined;
  }
  return out;
}

/**
 * Parallel-arm heads forking off a delay node: its group-node children on the fork-arm
 * options 'three'..'six' (FORK_ARM_OPTIONS). 'one' is the main spine and 'two' is the
 * wait_for_connect No branch (which lives on the wfc node, not here) — neither is an arm.
 * Only delay / wait_for_connect children count; action children live in `group`.
 */
function groupNodeArms(node: LocalGraphNode, graph: TouchGraph): string[] {
  const arms: string[] = [];
  for (const opt of FORK_ARM_OPTIONS) {
    const id = node.next[opt];
    if (id == null) continue;
    const child = graph.nodes[id];
    if (child == null) continue;
    if (child.type === 'delay' || child.type === 'wait_for_connect') arms.push(id);
  }
  return arms;
}

/** Action (runnable) steps in a group — the delay / wait_for_connect chain nodes filtered out. */
function actionStepsOf(
  group: string[],
  graph: TouchGraph,
  statusByTemplateStepId: StatusByTemplateStepId | null = null,
  campaignNameById: ReadonlyMap<string, string> | null = null,
): TouchGraphStep[] {
  const out: TouchGraphStep[] = [];
  for (const id of group) {
    const node = graph.nodes[id];
    if (node == null) continue;
    if (node.type === 'delay' || node.type === 'wait_for_connect') continue;
    out.push(toTouchStep(node, statusByTemplateStepId, campaignNameById));
  }
  return out;
}

/** Map a raw graph node to the display/edit-ready step shape. */
function toTouchStep(
  node: LocalGraphNode,
  statusByTemplateStepId: StatusByTemplateStepId | null = null,
  campaignNameById: ReadonlyMap<string, string> | null = null,
): TouchGraphStep {
  // An instance-driven node (a contact's cadence step) carries fewer fields than
  // a template node — the switch / connect / personalization config isn't part of
  // the per-contact step's SVR shape. Default those so a contact's graph renders
  // its content while structural extras (switch bands, auto-withdraw) fall back to
  // sensible values rather than throwing on `undefined`.
  const triggerCadenceTemplateIds = node.triggerCadenceTemplateIds ?? [];
  return {
    id: node.id,
    type: node.type,
    body2: node.body2,
    subject: node.subject,
    evaluatedBody: node.evaluatedBody ?? null,
    evaluatedSubject: node.evaluatedSubject ?? null,
    postsToLike: node.postsToLike,
    cc: node.cc,
    bcc: node.bcc,
    siftConditions: node.siftConditions,
    onlyOpenLink: node.onlyOpenLink,
    isPersonalized: node.isPersonalized ?? false,
    personalizationPrompt: node.personalizationPrompt ?? null,
    replyToPrevious: node.replyToPrevious ?? true,
    autoWithdraw: node.autoWithdraw ?? true,
    triggerCadenceTemplateIds,
    triggerCadenceTemplateNames: triggerCadenceTemplateIds.map((id) => campaignNameById?.get(id) ?? id),
    switchAttribute: node.switchAttribute ?? null,
    switchBands: node.switchBands ?? null,
    error: node.computedError?.default ?? node.computedError?.body2 ?? node.computedError?.subject ?? null,
    // Graph nodes are keyed by template step id, so a contact's status map (keyed
    // by templateStepId) joins directly on `node.id`. Absent → null (template page).
    contactStatus: statusByTemplateStepId?.[node.id] ?? null,
  };
}
